import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import {
  RoleModalProps,
  PermissionResponse,
  RoleDTO,
  FormData
} from '../types/types';
import {
  CREATE_ROLE,
  UPDATE_ROLE,
  GET_ALL_PERMISSIONS
} from 'services/api/role';
import toast from 'react-hot-toast';
import { toSnakeCase } from '../utils';
import { useQuery } from 'react-query';
import PermissionsAccordion from './PermissionsAccordion';

const RoleModal: React.FC<RoleModalProps> = ({
  show,
  onHide,
  role,
  onSuccess
}) => {
  const isEditMode = !!role;
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [permissionsByCategory, setPermissionsByCategory] = useState<
    Record<string, any>
  >({});

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
      permissions: []
    }
  });

  const { isLoading: permissionsLoading } = useQuery(
    ['permissions'],
    GET_ALL_PERMISSIONS,
    {
      enabled: show,
      onSuccess: response => {
        if (response && response.data) {
          setPermissionsByCategory(response.data);

          const flatPermissions: PermissionResponse[] = [];

          Object.entries(response.data).forEach(
            ([, categoryData]: [string, any]) => {
              if (
                categoryData._permissions &&
                Array.isArray(categoryData._permissions)
              ) {
                flatPermissions.push(...categoryData._permissions);
              }

              if (categoryData._subcategories) {
                Object.entries(categoryData._subcategories).forEach(
                  ([, subcategoryData]: [string, any]) => {
                    if (subcategoryData.permissions) {
                      Object.values(subcategoryData.permissions).forEach(
                        (permArray: any) => {
                          if (Array.isArray(permArray)) {
                            flatPermissions.push(...permArray);
                          }
                        }
                      );
                    }

                    if (subcategoryData._nestedSubcategories) {
                      Object.entries(
                        subcategoryData._nestedSubcategories
                      ).forEach(([, nestedSubcategoryData]: [string, any]) => {
                        if (
                          nestedSubcategoryData.stages &&
                          Array.isArray(nestedSubcategoryData.stages)
                        ) {
                          nestedSubcategoryData.stages.forEach((stage: any) => {
                            flatPermissions.push({
                              _id: stage.id,
                              key: stage.key,
                              name: stage.name,
                              isActive: stage.isActive,
                              category: categoryData._category,
                              subcategory:
                                subcategoryData._subCategoryDisplayName || '',
                              nestedSubcategory:
                                nestedSubcategoryData._nestedSubcategoryDisplayName ||
                                '',
                              action: stage.name,
                              createdAt: new Date().toISOString(),
                              updatedAt: new Date().toISOString()
                            });
                          });
                        }

                        if (nestedSubcategoryData.permissions) {
                          Object.entries(
                            nestedSubcategoryData.permissions
                          ).forEach(([, permArray]: [string, any]) => {
                            if (Array.isArray(permArray)) {
                              flatPermissions.push(...permArray);
                            }
                          });
                        }
                      });
                    }
                  }
                );
              }
            }
          );

          if (isEditMode && role?.permissions) {
            const permissionIds = Array.isArray(role.permissions)
              ? role.permissions.map(p =>
                  typeof p === 'string' ? p : (p as any).id || p._id
                )
              : [];

            const prefilledPermissions = flatPermissions.filter(permission =>
              permissionIds.includes((permission as any).id || permission._id)
            );

            reset({
              name: role.name || '',
              description: role.description || '',
              permissions: prefilledPermissions
            });
          } else {
            reset({
              name: '',
              description: '',
              permissions: []
            });
          }
        }
      },
      onError: err => {
        console.error('Error fetching permissions:', err);
        toast.error('Failed to fetch permissions');
      }
    }
  );

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);
    try {
      const key = toSnakeCase(data.name);

      const formattedData = {
        ...data,
        key,
        permissions: data.permissions
          .map(p => (p as any).id || p._id)
          .filter(Boolean)
      };

      if (isEditMode && ((role as any)?._id || (role as any)?.id)) {
        await UPDATE_ROLE({
          roleId: (role as any).id || role._id,
          data: formattedData as RoleDTO
        });
        toast.success('Role updated successfully');
      } else {
        await CREATE_ROLE(formattedData as RoleDTO);
        toast.success('Role created successfully');
      }
      onSuccess();
      onHide();
    } catch (err) {
      setError((err as any).response.data.messages || 'An error occurred');
      toast.error((err as any).response.data.messages || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? 'Update Role' : 'Add New Role'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label className="required">Name</Form.Label>
            <Form.Control
              type="text"
              {...register('name', { required: 'Role name is required' })}
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register('description', {
                required: 'Description is required'
              })}
              isInvalid={!!errors.description}
            />
            <Form.Control.Feedback type="invalid">
              {errors.description?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="mb-3">
            <h5>Permissions</h5>
            <div className="overflow-auto" style={{ maxHeight: '400px' }}>
              <Controller
                control={control}
                name="permissions"
                render={({ field }) => (
                  <PermissionsAccordion
                    permissionsByCategory={permissionsByCategory}
                    field={field}
                    isLoading={permissionsLoading}
                  />
                )}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Close
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default RoleModal;
